import React, { cloneElement, SyntheticEvent, useRef, useState } from "react";
import { ClickAwayListener, Tooltip } from "@mui/material";

import { FocusableTooltipProps } from "./types";

// Hover alone never reaches keyboard or touch users, so the target takes focus and opens on tap; the tooltip describes it, since a generic span cannot be named.
export const FocusableTooltip: React.FC<FocusableTooltipProps> = ({
  title,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isDismissedRef = useRef(false);

  const open = () => {
    isDismissedRef.current = false;
    setIsOpen(true);
  };
  // Escape must stick while the pointer rests on the target, or a pending hover reopens it.
  const openOnHoverOrFocus = (event: SyntheticEvent) => {
    if (isDismissedRef.current && event.type === "mouseover") {
      return;
    }
    open();
  };
  const close = (event: Event | SyntheticEvent) => {
    isDismissedRef.current = event.type === "keydown";
    setIsOpen(false);
  };

  const target = cloneElement(children, {
    tabIndex: 0,
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      children.props.onClick?.(event);
      open();
    },
    onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
      children.props.onMouseLeave?.(event);
      isDismissedRef.current = false;
    },
  });

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Tooltip
        title={title}
        arrow
        describeChild
        open={isOpen}
        onOpen={openOnHoverOrFocus}
        onClose={close}
        disableTouchListener
      >
        {target}
      </Tooltip>
    </ClickAwayListener>
  );
};
